import React, { memo } from 'react';
import { HardDrive } from 'lucide-react';
import { FileStat } from '../../api/IFileSystem';
import { useFileIcon } from '../../hooks/useFileIcon';
import { formatSize } from '../../utils/fileUtils';
import { t } from '../../i18n';

interface DriveSize {
  total_size: number;
  free_size: number;
  used_size: number;
}

interface DriveCardProps {
  file: FileStat;
  selected: boolean;
  driveSize?: DriveSize | null;
  onClick: (e: React.MouseEvent) => void;
  onDoubleClick: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
}

export const DriveCard: React.FC<DriveCardProps> = memo(({
  file,
  selected,
  driveSize,
  onClick,
  onDoubleClick,
  onContextMenu,
}) => {
  const iconUrl = useFileIcon(file, 48);

  // Calculate usage percentage
  const usagePercent = driveSize && driveSize.total_size > 0
    ? (driveSize.used_size / driveSize.total_size) * 100
    : 0;

  // Determine bar color based on usage
  const getBarColor = (percent: number) => {
    if (percent >= 90) return 'bg-red-500';
    if (percent >= 75) return 'bg-yellow-500';
    return 'bg-blue-500';
  };

  return (
    <div
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onContextMenu(e);
      }}
      data-path={file.path}
      className={`
        flex items-center p-3 rounded-lg cursor-pointer border transition-all duration-200
        min-w-[240px] max-w-[320px]
        ${selected
          ? 'bg-blue-500/20 border-blue-500/50'
          : 'bg-white/5 border-transparent hover:bg-white/10 hover:border-white/20'
        }
      `}
    >
      {/* Drive Icon */}
      <div className="mr-4 flex-shrink-0">
        {iconUrl ? (
          <img src={iconUrl} alt={file.name} className="w-12 h-12 object-contain" draggable={false} />
        ) : (
          <HardDrive size={48} className="text-slate-400" />
        )}
      </div>

      {/* Drive Info */}
      <div className="flex-1 min-w-0">
        {/* Drive Name */}
        <div className="text-sm font-medium text-white truncate mb-1">
          {file.name}
        </div>

        {/* Usage Bar */}
        {driveSize && driveSize.total_size > 0 ? (
          <>
            <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden mb-1">
              <div
                className={`h-full ${getBarColor(usagePercent)} transition-all duration-300`}
                style={{ width: `${Math.min(usagePercent, 100)}%` }}
              />
            </div>
            {/* Size Info */}
            <div className="text-xs text-white/50">
              {formatSize(driveSize.free_size)} {t('driveFree')}, {t('driveTotal')} {formatSize(driveSize.total_size)}
            </div>
          </>
        ) : (
          <div className="text-xs text-white/30">
            {t('driveLoading')}
          </div>
        )}
      </div>
    </div>
  );
});

DriveCard.displayName = 'DriveCard';

export default DriveCard;
